import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Card, EmptyState } from "@/components/primitives";
import { DatePicker } from "@/components/ui/date-picker";
import { toast } from "sonner";
import { LogOut, RefreshCcw, ArrowRight, Receipt, FileText, CheckCircle2, Wallet } from "lucide-react";
import { CheckoutFlow } from "@/components/checkout/CheckoutFlow";
import { BTN_SECONDARY, BTN_SOFT, BTN_SM } from "@/components/checkout/buttonStyles";
import { useStayCheckedOut } from "@/hooks/useStayCheckedOut";
import { docsAPI, openPdf, fmtINR } from "@/lib/documents";
import { DownloadPdfButton } from "@/components/documents/DownloadPdfButton";

const fmtDay = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : "—");
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" }) : "—");

export default function CheckoutQueue() {
  const nav = useNavigate();
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [board, setBoard] = useState({ pending: [], completed: [], kpis: {} });
  const [loading, setLoading] = useState(false);
  const [flowStay, setFlowStay] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/checkout/board?date=${date}`); setBoard(data); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [date]);
  useEffect(() => { load(); }, [load]);
  useStayCheckedOut(load);

  const k = board.kpis || {};
  return (
    <div data-testid="checkout-queue-page">
      <PageHeader
        title="Checkout Queue"
        subtitle="Guests due out today and everyone already checked out — one flow for every desk"
        testid="checkout-queue-header"
        action={
          <div className="flex items-center gap-2">
            <DatePicker value={date} onChange={(v) => v && setDate(v)} data-testid="checkout-queue-date" />
            <button type="button" className={`${BTN_SECONDARY} ${BTN_SM}`} onClick={load} data-testid="checkout-queue-refresh">
              <RefreshCcw className="w-4 h-4" />Refresh
            </button>
          </div>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiTile label="Pending" value={k.pending ?? 0} testid="kpi-pending" />
        <KpiTile label="Overdue" value={k.overdue ?? 0} tone={k.overdue ? "error" : "neutral"} testid="kpi-overdue" />
        <KpiTile label="With Balance" value={k.with_balance ?? 0} tone={k.with_balance ? "warning" : "neutral"} testid="kpi-with-balance" />
        <KpiTile label="Completed Today" value={k.completed ?? 0} tone="success" testid="kpi-completed" />
      </div>

      <Section title="Pending" count={board.pending.length} testid="checkout-pending-section" loading={loading}
        empty={<EmptyState icon={LogOut} title="No pending check-outs" subtitle="Every guest due out has been checked out." />}>
        {board.pending.map((x) => <PendingCard key={x.stay_id} item={x} onCheckout={() => setFlowStay(x)} onOpen={() => nav(`/pms/stays/${x.stay_id}`)} />)}
      </Section>

      <Section title="Completed Today" count={board.completed.length} testid="checkout-completed-section" loading={loading}
        empty={<EmptyState icon={CheckCircle2} title="No check-outs completed yet" subtitle="Completed check-outs will appear here with their receipt." />}>
        {board.completed.map((x) => <CompletedCard key={x.stay_id} item={x} onOpen={() => nav(`/pms/stays/${x.stay_id}`)} />)}
      </Section>

      {flowStay && (
        <CheckoutFlow stayId={flowStay.stay_id} hotelId={flowStay.hotel_id} open onCancel={() => setFlowStay(null)}
          onSuccess={() => { setFlowStay(null); load(); }} />
      )}
    </div>
  );
}

function Section({ title, count, children, empty, testid, loading }) {
  return (
    <section className="mb-8" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <h2 className="text-base md:text-lg font-semibold text-[#1A2425]">{title}</h2>
        <span className="text-[11px] mono px-1.5 py-0.5 rounded bg-[#F2FCFC] border border-[#D5E6E8] text-[#3F5257]">{count}</span>
        {loading && <span className="text-[11px] text-[#97A6A9]">loading…</span>}
      </div>
      {count === 0 ? empty : <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">{children}</div>}
    </section>
  );
}

function StatusBadge({ item }) {
  if (item.status === "checked_out") {
    return (
      <span data-testid={`queue-status-${item.stay_code}`} className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-[#DBFCEF] text-[#014751]">
        <span className="w-1.5 h-1.5 rounded-full bg-[#14C48E]" />Checked Out
      </span>
    );
  }
  const overdue = item.is_overdue;
  return (
    <span data-testid={`queue-status-${item.stay_code}`} className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full ${overdue ? "bg-[#FEE2E2] text-[#991B1B]" : "bg-[#FEF3C7] text-[#78530E]"}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${overdue ? "bg-[#DC2626]" : "bg-[#F59E0B]"}`} />{overdue ? "Overdue" : "Due Out"}
    </span>
  );
}

function CardShell({ item, children, footer }) {
  return (
    <Card className={`p-4 flex flex-col gap-1 ${item.status !== "checked_out" && item.balance_due > 0 ? "border-l-[3px] border-l-amber-400" : ""}`} data-testid={`checkout-card-${item.stay_code}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="text-[15px] font-bold text-[#1A2425] truncate" data-testid={`queue-guest-${item.stay_code}`}>{item.guest_name || "Guest"}</div>
        <StatusBadge item={item} />
      </div>
      <div className="text-[13px] text-[#3F5257]" data-testid={`queue-room-${item.stay_code}`}>
        {item.room_number ? `Room ${item.room_number}` : "No room"}{item.room_type ? ` — ${item.room_type}` : ""}
      </div>
      {children}
      <div className="text-[11px] mono text-[#97A6A9] truncate" data-testid={`queue-ref-${item.stay_code}`}>{item.stay_code}{item.session_id ? ` · session ${item.session_status}` : ""}</div>
      <div className="flex flex-wrap justify-end gap-2 mt-1">{footer}</div>
    </Card>
  );
}

function PendingCard({ item, onCheckout, onOpen }) {
  return (
    <CardShell item={item} footer={<>
      <button type="button" className={`${BTN_SECONDARY} ${BTN_SM}`} onClick={onOpen} data-testid={`checkout-open-${item.stay_code}`}>Stay<ArrowRight size={13} /></button>
      <button type="button" className={`${BTN_SOFT} ${BTN_SM}`} onClick={onCheckout} data-testid={`checkout-start-${item.stay_code}`}><LogOut size={13} />Check Out</button>
    </>}>
      <div className="text-[12px] text-[#97A6A9]">Checked in: {fmtDay(item.check_in)}{item.nights ? ` · ${item.nights} night${item.nights === 1 ? "" : "s"}` : ""} · Due out: {fmtDay(item.check_out)}</div>
      {item.balance_due > 0 && (
        <div className="inline-flex items-center gap-1 text-[11px] mono uppercase px-1.5 py-0.5 rounded bg-[#FEF3C7] text-[#78530E] font-semibold w-fit" data-testid={`queue-balance-${item.stay_code}`}>
          <Wallet className="w-2.5 h-2.5" />Balance due {fmtINR(item.balance_due)}
        </div>
      )}
    </CardShell>
  );
}

function CompletedCard({ item, onOpen }) {
  return (
    <CardShell item={item} footer={<>
      <button type="button" className={`${BTN_SECONDARY} ${BTN_SM}`} onClick={onOpen} data-testid={`checkout-open-${item.stay_code}`}>Stay<ArrowRight size={13} /></button>
      {item.bill_id && <button type="button" className={`${BTN_SECONDARY} ${BTN_SM}`} onClick={() => openPdf(docsAPI.billPdfUrl(item.bill_id), "Invoice.pdf")} data-testid={`queue-bill-${item.stay_code}`}><FileText size={13} />Final Bill</button>}
      {item.bill_id && <DownloadPdfButton url={docsAPI.billPdfUrl(item.bill_id)} filename="Invoice.pdf" label="Download Bill" testId={`queue-bill-download-${item.stay_code}`} />}
      {item.receipt_id && <button type="button" className={`${BTN_SECONDARY} ${BTN_SM}`} onClick={() => openPdf(docsAPI.receiptPdfUrl(item.receipt_id), "Receipt.pdf")} data-testid={`queue-receipt-${item.stay_code}`}><Receipt size={13} />View Receipt</button>}
      {item.receipt_id && <DownloadPdfButton url={docsAPI.receiptPdfUrl(item.receipt_id)} filename="Receipt.pdf" label="Download Receipt" testId={`queue-receipt-download-${item.stay_code}`} />}
    </>}>
      <div className="text-[12px] text-[#97A6A9]" data-testid={`queue-outat-${item.stay_code}`}>Checked out: {fmtTime(item.checked_out_at)} · {fmtDay(item.check_in)} → {fmtDay(item.checked_out_at || item.check_out)}</div>
    </CardShell>
  );
}

function KpiTile({ label, value, tone = "neutral", testid }) {
  return (
    <Card className="p-5" data-testid={testid}>
      <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-hocp-textTertiary mb-3">{label}</div>
      <div className={`font-display text-[26px] font-bold tabular-nums leading-none ${tone === "error" ? "text-rose-600" : tone === "warning" ? "text-amber-600" : tone === "success" ? "text-[#14805E]" : "text-hocp-textPrimary"}`}>{value}</div>
    </Card>
  );
}
